import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import Logo from "../assets/Images/Logo-white.png";
import Logo2 from "../assets/Images/Logo-white.png";
import MainLogo from "../assets/Images/main-Logo.png";
import OverlayBanner from "../assets/Images/OverlayBanner.png";
import partnerLogo from "../assets/Images/partner-logo.png";
const Home = () => {
  const { matchId, court } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [showPartnerLogo, setShowPartnerLogo] = useState(false);

  // Partner logo display logic - show for 7 seconds every 15 minutes
  useEffect(() => {
    const showPartnerLogoInterval = () => {
      setShowPartnerLogo(true);

      // Hide partner logo after 7 seconds
      setTimeout(() => {
        setShowPartnerLogo(false);
      }, 7000);
    };

    // Show partner logo immediately on component mount
    showPartnerLogoInterval();

    // Set up interval to show partner logo every 15 minutes 900000 (900,000 ms)
    const interval = setInterval(showPartnerLogoInterval, 900000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Socket.IO connection
  useEffect(() => {
    const newSocket = io(
      "https://api-third-shot-score-684526581556.asia-south1.run.app",
      {
        transports: ["websocket", "polling"],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxReconnectionAttempts: 10,
        forceNew: true,
      }
    );

    // Connection event handlers
    newSocket.on("connect", () => {
      console.log("✅ WebSocket connected successfully");
      // Join specific match room or court room
      if (matchId) {
        newSocket.emit("joinMatch", matchId);
      } else if (court) {
        newSocket.emit("joinCourt", court);
      }
    });

    newSocket.on("disconnect", (reason) => {
      console.log("❌ WebSocket disconnected:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("❌ WebSocket connection error:", error);
    });

    newSocket.on("reconnect", (attemptNumber) => {
      console.log("🔄 WebSocket reconnected after", attemptNumber, "attempts");
      // Re-join specific match room or court room after reconnection
      if (matchId) {
        newSocket.emit("joinMatch", matchId);
      } else if (court) {
        newSocket.emit("joinCourt", court);
      }
    });

    newSocket.on("reconnect_error", (error) => {
      console.error("❌ WebSocket reconnection error:", error);
    });

    newSocket.on("reconnect_failed", () => {
      console.error("❌ WebSocket reconnection failed");
    });

    setSocket(newSocket);

    // Set up periodic heartbeat to keep connection alive
    const heartbeatInterval = setInterval(() => {
      if (newSocket.connected) {
        newSocket.emit("ping");
      }
    }, 30000); // Send ping every 30 seconds

    return () => {
      clearInterval(heartbeatInterval);
      if (matchId) {
        newSocket.emit("leaveMatch", matchId);
      } else if (court) {
        newSocket.emit("leaveCourt", court);
      }
      newSocket.close();
    };
  }, [matchId, court]);

  // Fetch match data on component mount
  useEffect(() => {
    if (matchId || court) {
      fetchMatch();
    }
  }, [matchId, court]);

  // Handle Socket.IO messages
  useEffect(() => {
    if (!socket || (!matchId && !court)) return;

    socket.on("scoreUpdate", (data) => {
      if (matchId && data.matchId === parseInt(matchId)) {
        setMatch(data.match);
      } else if (court && data.match && data.match.court === court) {
        setMatch(data.match);
      }
    });

    socket.on("setEnd", (data) => {
      if (matchId && data.matchId === parseInt(matchId)) {
        setMatch(data.match);
      } else if (court && data.match && data.match.court === court) {
        setMatch(data.match);
      }
    });

    socket.on("serveUpdate", (data) => {
      if (matchId && data.matchId === parseInt(matchId)) {
        setMatch(data.match);
      } else if (court && data.match && data.match.court === court) {
        setMatch(data.match);
      }
    });

    socket.on("matchEnd", (data) => {
      if (matchId && data.matchId === parseInt(matchId)) {
        setMatch(data.match);
      } else if (court && data.match && data.match.court === court) {
        // For court-based URLs, immediately clear the match to show "No active match"
        setMatch(null);
      }
    });

    socket.on("matchCreated", (data) => {
      console.log("📺 Overlay: Received matchCreated event:", data);
      if (court && data.match && data.match.court === court) {
        console.log("📺 Overlay: Setting match for court:", court);
        setMatch(data.match);
      } else if (
        matchId &&
        data.match &&
        data.match.matchId === parseInt(matchId)
      ) {
        console.log("📺 Overlay: Setting match for matchId:", matchId);
        setMatch(data.match);
      }
    });

    socket.on("courtCleared", (data) => {
      if (court && data.court === court) {
        setMatch(null);
      }
    });

    socket.on("undoScore", (data) => {
      if (matchId && data.matchId === parseInt(matchId)) {
        setMatch(data.match);
      } else if (court && data.match && data.match.court === court) {
        setMatch(data.match);
      }
    });

    return () => {
      socket.off("scoreUpdate");
      socket.off("setEnd");
      socket.off("serveUpdate");
      socket.off("matchEnd");
      socket.off("matchCreated");
      socket.off("courtCleared");
      socket.off("undoScore");
    };
  }, [socket, matchId, court]);

  const fetchMatch = async () => {
    try {
      let response;
      if (matchId) {
        response = await axios.get(
          `https://api-third-shot-score-684526581556.asia-south1.run.app/pickleball/match/${matchId}`
        );
      } else if (court) {
        response = await axios.get(
          `https://api-third-shot-score-684526581556.asia-south1.run.app/pickleball/court/${court}`
        );
      }

      if (response && response.data.success) {
        setMatch(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching match:", error);
      // For court-based URLs, don't show error if no active match
      if (!court) {
        console.error("Match not found");
      }
    } finally {
      setLoading(false);
    }
  };

  const getSetScore = (sets, setNumber, team) => {
    const set = sets?.find((s) => s?.setNumber === setNumber);
    if (!set) return "";

    if (team === "A") {
      return set.teamAScore.toString();
    } else {
      return set.teamBScore.toString();
    }
  };

  const getTotalSets = (sets, team, teamA, teamB) => {
    return sets?.filter((set) => set?.winner === (team === "A" ? teamA : teamB))
      ?.length;
  };

  const isSetWinner = (sets, setNumber, teamName) => {
    const set = sets.find((s) => s.setNumber === setNumber);
    return set && set.winner === teamName;
  };

  const shouldShowSet = (match, setNumber) => {
    // For active matches, only show current set and past sets
    // Don't show future sets (display as 0)
    console.log(setNumber <= match?.currentSet);
    // Show set if it's current or past set, OR if any team has scored in this set
    const set = match?.sets?.find((s) => s.setNumber === setNumber);
    const hasScore = set && (set?.teamAScore > 0 || set?.teamBScore > 0);

    // For active matches, always show the current set even if no one has scored yet
    if (match?.status === "active" && setNumber === match?.currentSet) {
      return true;
    }

    // For completed matches or past sets, only show if there's a score
    if (match?.status !== "active") {
      return setNumber <= match?.currentSet && hasScore;
    } else {
      return setNumber <= match?.currentSet && hasScore;
    }
  };

  if (loading) {
    return (
      <div style={{ background: "black", height: "100vh" }}>
        <div style={{ position: "absolute", top: 50, left: 40 }}>
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div style={{ background: "black", height: "100vh" }}>
        <img
          src={"https://storage.googleapis.com/pllayer-assests/OverlayBanner.png"}
          alt="Overlay Banner"
          style={{ height: "100vh" }}
        />
      </div>
    );
  }

  const teamASets = getTotalSets(match.sets, "A", match.teamA, match.teamB);
  const teamBSets = getTotalSets(match.sets, "B", match.teamA, match.teamB);

  return (
    <div>
      <div style={{ background: "transparent", height: "100vh" }}>
        {/* Scorecard Section */}
        <div style={{ position: "absolute", top: 12, left: 40, right: 40 }}>
          {/* Scorecard Container */}
          <div
            style={{
              borderRadius: "8px",
              padding: "20px",
              display: "flex",
              alignItems: "center",
              gap: "0",
              width: "max-content",
              position: "relative",
            }}
          >
            {/* Left Section - Logo */}
            <div
              style={{
                background: "#D9E01F",

                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",

                width: "124px",
                height: "109px",
                transform: "rotate(0deg)",
                opacity: 1,
                borderRightWidth: "1px",
              }}
            >
              <img
                src={"https://storage.googleapis.com/pllayer-assests/main-Logo.png"}
                alt="Team Logo"
                style={{
                  height: "85px",
                  width: "96px",
                  objectFit: "contain",
                  transform: "rotate(0deg)",
                  opacity: 1,
                }}
              />
            </div>

            {/* Middle Section - Player Names */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
                width: "max-content",
                height: "109px",
                transform: "rotate(0deg)",
                opacity: 1,
                borderRightWidth: "1px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  background: "black",
                  alignItems: "center",
                  padding: "0px 20px",
                  gap: "10px",
                  borderBottom: "1px solid white",

                  width: "100%",
                  height: "59px",
                  transform: "rotate(0deg)",
                  opacity: 1,
                }}
              >
                <div
                  style={{
                    width: "13px",
                    height: "13px",
                    borderRadius: "50%",
                    background: match.teamAServe ? "#B1FF32" : "transparent",
                  }}
                ></div>
                <span
                  style={{
                    color: "white",

                    fontWeight: "700",
                    fontStyle: "Bold",
                    fontSize: "18px",
                    lineHeight: "max-content",
                    letterSpacing: "0%",
                    textTransform: "uppercase",
                  }}
                  className="bebas-neue-regular"
                >
                  {match.teamA}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0px 20px",
                  background: "black",
                  gap: "10px",

                  width: "100%",
                  height: "59px",
                  transform: "rotate(0deg)",
                  opacity: 1,
                }}
              >
                <div
                  style={{
                    width: "13px",
                    height: "13px",
                    borderRadius: "50%",
                    background: match.teamBServe ? "#B1FF32" : "transparent",
                  }}
                ></div>
                <span
                  style={{
                    color: "white",

                    fontWeight: "700",
                    fontStyle: "Bold",
                    fontSize: "18px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    textTransform: "uppercase",
                  }}
                  className="bebas-neue-regular"
                >
                  {match.teamB}
                </span>
              </div>
            </div>

            {/* Right Section - Scores */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "102px",
                position: "absolute",
                right: "-82px",
                top: "20px",
              }}
            >
              {/* Top Row - Team A Scores */}
              <div
                style={{
                  display: "flex",
                  gap: "2px",
                  height: "50px",
                }}
              >
                {/* <div
                  style={{
                    background: "#292C42",
                    color: "white",
                    fontSize: "18px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    minWidth: "48px",
                    height: "53px",
                    opacity: 1,
                    borderWidth: "1px",
                    borderRight: "1px solid white",
                  }}
                >
                  {teamASets}
                </div> */}
                {shouldShowSet(match, 1) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 1, "A")}
                  </div>
                )}
                {shouldShowSet(match, 2) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 2, "A")}
                  </div>
                )}
                {shouldShowSet(match, 3) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 3, "A")}
                  </div>
                )}
              </div>

              {/* Middle Row - Team B Scores */}
              <div
                style={{
                  display: "flex",
                  gap: "2px",
                  height: "50px",
                }}
              >
                {/* <div
                  style={{
                    background: "#292C42",
                    color: "white",
                    fontSize: "18px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "6px",
                    width: "48px",
                    minWidth: "48px",
                    height: "53px",
                    opacity: 1,
                    borderWidth: "1px",
                    borderRight: "1px solid white",
                  }}
                >
                  {teamBSets}
                </div> */}
                {shouldShowSet(match, 1) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "6px",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 1, "B")}
                  </div>
                )}
                {shouldShowSet(match, 2) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "6px",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 2, "B")}
                  </div>
                )}
                {shouldShowSet(match, 3) && (
                  <div
                    style={{
                      background: "#D9E01F",
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "900",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "6px",
                      width: "48px",
                      minWidth: "48px",
                      height: "53px",
                      opacity: 1,
                    }}
                  >
                    {getSetScore(match.sets, 3, "B")}
                  </div>
                )}
              </div>

              {/* Bottom Row - Headers */}
              <div
                style={{
                  display: "flex",
                  marginTop: "9px",
                  height: "40px",
                  gap: "2px",
                  left: "-55px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    background: "#0E0D15",
                    color: "white",
                    fontSize: "17px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "128px",
                    minWidth: "48px",
                    height: "38px",
                    opacity: 1,
                    borderRightWidth: "1px",
                  }}
                >
                  SETS{" "}
                  {shouldShowSet(match, 1)
                    ? `1 - 0`
                    : shouldShowSet(match, 2)
                    ? `2 - 0`
                    : shouldShowSet(match, 3)
                    ? `3 - 0`
                    : `0 - 0`}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Logo */}
        {/* <div
          style={{
            position: "absolute",
            bottom: 50,
            right: showPartnerLogo ? 0 : 40,
          }}
        >
          <img
            src={showPartnerLogo ? partnerLogo : Logo}
            alt={showPartnerLogo ? "partner logo" : "logo"}
            height={37}
            style={{
              padding: showPartnerLogo ? "0px 40px" : "0px",
            }}
            width={showPartnerLogo ? "100%" : 270}
          />
        </div> */}

        {/* Top Right Logo */}
        <div style={{ position: "absolute", bottom: 35, right: 50 }}>
          <img
            src={
              "https://storage.googleapis.com/pllayer-assests/Logo-white.png"
            }
            alt="logo2"
            height={41}
            width={301}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;
